import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck } from 'react-icons/lu';
import { beatIndexAt } from './timeline';
import { STAGE_H, STAGE_W, clamp01, easeInOut } from './stageLayout';
import AddJobScene from './scenes/AddJobScene';
import AddVisitScene from './scenes/AddVisitScene';
import CalendarScene from './scenes/CalendarScene';
import TitleScene from './scenes/TitleScene';
import VisitFormScene from './scenes/VisitFormScene';

const CLICK_MS = 520;

/**
 * Layout width of the stage box. Not getBoundingClientRect: the dialog opens
 * with a scale animation, and a width read mid-animation would stick (a
 * transform doesn't trigger the ResizeObserver again).
 */
const useLayoutWidth = (ref, initial) => {
  const [width, setWidth] = useState(initial);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    setWidth(element.clientWidth || initial);
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, initial]);
  return width;
};

/**
 * Every cursor keyframe of the video on one time line. The cursor rests on a
 * beat's last point until the beat ends, so it only heads for the next target
 * once the narration has moved on.
 */
const buildCursorTrack = (beats) => beats
  .flatMap((beat) => {
    const frames = (beat.cursor || []).map((frame) => ({ ...frame, time: beat.start + frame.p * beat.duration }));
    const last = frames[frames.length - 1];
    if (last && last.p < 1) frames.push({ x: last.x, y: last.y, p: 1, time: beat.start + beat.duration - 1 });
    return frames;
  })
  .sort((a, b) => a.time - b.time);

const cursorAt = (track, time) => {
  if (track.length === 0) return null;
  const nextIndex = track.findIndex((frame) => frame.time > time);
  if (nextIndex === -1) return track[track.length - 1];
  if (nextIndex === 0) return track[0];
  const from = track[nextIndex - 1];
  const to = track[nextIndex];
  const k = easeInOut(clamp01((time - from.time) / Math.max(1, to.time - from.time)));
  return { x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k };
};

const lastClick = (track, time) => {
  for (let index = track.length - 1; index >= 0; index -= 1) {
    const frame = track[index];
    if (frame.time <= time) {
      if (frame.click && time - frame.time < CLICK_MS) return { ...frame, progress: (time - frame.time) / CLICK_MS };
      if (time - frame.time >= CLICK_MS) return null;
    }
  }
  return null;
};

const Cursor = ({ point, click }) => (
  <>
    {click && (
      <span
        style={{ left: click.x - 28, top: click.y - 28, opacity: 1 - click.progress, transform: `scale(${0.4 + click.progress * 0.9})` }}
        className="tw-pointer-events-none tw-absolute tw-z-40 tw-h-14 tw-w-14 tw-rounded-full tw-border-[3px] tw-border-teal-400 tw-bg-teal-400/25"
      />
    )}
    <svg
      style={{ left: point.x - 3, top: point.y - 2, transform: `scale(${click && click.progress < 0.35 ? 0.86 : 1})` }}
      className="tw-pointer-events-none tw-absolute tw-z-50 tw-h-9 tw-w-9 tw-origin-top-left tw-drop-shadow-[0_3px_6px_rgba(0,0,0,0.35)]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M4 2.5 L4 19 L8.6 14.6 L11.7 21.5 L14.6 20.2 L11.6 13.5 L18 13.5 Z" fill="#ffffff" stroke="#0f172a" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  </>
);

Cursor.propTypes = { point: PropTypes.object.isRequired, click: PropTypes.object };

/** Dims the stage around the rectangle being talked about. */
const Spotlight = ({ rect }) => {
  const lastRef = useRef(rect);
  if (rect) lastRef.current = rect;
  const shown = lastRef.current;
  if (!shown) return null;
  return (
    <span
      style={{ left: shown.x, top: shown.y, width: shown.w, height: shown.h, opacity: rect ? 1 : 0 }}
      className="tw-pointer-events-none tw-absolute tw-z-30 tw-rounded-2xl tw-shadow-[0_0_0_9999px_rgba(15,23,42,0.34)] tw-ring-[3px] tw-ring-teal-400 tw-transition-all tw-duration-500 tw-ease-out"
    />
  );
};

Spotlight.propTypes = { rect: PropTypes.object };

const StageToast = ({ toast }) => (
  <div
    className={`tw-pointer-events-none tw-absolute tw-right-6 tw-top-6 tw-z-40 tw-flex tw-w-[330px] tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-white/80 tw-bg-white tw-px-4 tw-py-3.5 tw-shadow-2xl tw-transition-all tw-duration-300 dark:tw-border-white/10 dark:tw-bg-[#262a34] ${
      toast ? 'tw-translate-y-0 tw-opacity-100' : '-tw-translate-y-4 tw-opacity-0'
    }`}
  >
    <LuCircleCheck className="tw-h-6 tw-w-6 tw-shrink-0 tw-text-emerald-500" />
    <span className="tw-text-[15px] tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{toast?.text}</span>
  </div>
);

StageToast.propTypes = { toast: PropTypes.object };

const SCENES = { calendar: CalendarScene, addVisit: AddVisitScene, visitForm: VisitFormScene, addJob: AddJobScene };

/**
 * The picture of the video at `time`: the scene of the current beat in its
 * state, the spotlight, notifications and the moving cursor - drawn on the
 * 1280×720 stage and scaled to the available width.
 */
const TutorialStage = ({ timeline, time }) => {
  const wrapperRef = useRef(null);
  const width = useLayoutWidth(wrapperRef, 960);
  const scale = width / STAGE_W;
  const track = useMemo(() => buildCursorTrack(timeline.beats), [timeline]);

  const index = beatIndexAt(timeline.beats, time);
  const beat = timeline.beats[index];
  const p = clamp01((time - beat.start) / beat.duration);
  const state = beat.state(p);
  const point = beat.cursor ? cursorAt(track, time) : null;
  const click = beat.cursor ? lastClick(track, time) : null;
  const Scene = SCENES[beat.scene];

  return (
    <div ref={wrapperRef} className="tw-relative tw-aspect-video tw-w-full tw-overflow-hidden tw-bg-[#f4f6f9] dark:tw-bg-[#14171c]">
      <div
        aria-hidden="true"
        style={{ width: STAGE_W, height: STAGE_H, transform: `scale(${scale})` }}
        className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-origin-top-left tw-select-none tw-font-sans"
      >
        {beat.scene === 'title' ? <TitleScene state={state} p={p} /> : <Scene state={state} />}
        <Spotlight rect={beat.spot ? beat.spot(p) : null} />
        <StageToast toast={beat.toast ? beat.toast(p) : null} />
        {point && <Cursor point={point} click={click} />}
      </div>
    </div>
  );
};

TutorialStage.propTypes = {
  timeline: PropTypes.shape({ beats: PropTypes.array.isRequired }).isRequired,
  time: PropTypes.number.isRequired,
};

export default TutorialStage;
